import * as Yup from 'yup';

export interface Tester {
    createdBy: string;
    createdDt: Date;
    updatedBy: string;
    updatedDt: Date;
    testerId: number;
    testerName: string;
    testerEffectiveDate: Date;
    physicalAddrLine1: string;
    physicalAddrLine2: string;
    physicalAddrCity: string;
    physicalAddrstateCD: string;
    physicalAddrZip: string;
    mailingAddrLine1: string;
    mailingAddrLine2: string;
    mailingAddrCity: string;
    mailingAddrstateCD: string;
    mailingAddrZip: string;
    comment: string;
    isActive: string;
    automatedVehiclesCount: number;
    pddsCount: number;
    testerContacts: TesterContact[];
    pocName: string;
    pocTitle: string;
    pocPhone: string;
    pocEmail: string;
}

export interface TesterContact {
    testerContactId: number;
    pocName: string;
    pocTitle: string;
    pocPhone: string;
    pocEmail: string;
    createdDt: Date;
    createdBy: string;
    updatedDt: Date;
    updatedBy: string;
}

const digitsOnly = (value: any): boolean => { return (value && value.trim().length > 0) ? /^\d+$/.test(value) : true };
const zipRegExp = (value: any): boolean => { return (value && value.trim().length > 0) ? /^\d{5}(?:-\d{4})?$/.test(value) : true };
const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

export const TesterValidationSchema = Yup.object().shape({
    testerName: Yup.string()
        .required("Required")
        .max(50, "Max Field Length: 50"),
    testerEffectiveDate: Yup.date()
        .required("Required"),
    physicalAddrLine1: Yup.string()
        .required("Required")
        .max(50, "Max Field Length: 50"),
    physicalAddrLine2: Yup.string()
        .optional()
        .max(50, "Max Field Length: 50"),
    physicalAddrCity: Yup.string()
        .required("Required")
        .max(50, "Max Field Length: 50"),
    physicalAddrstateCD: Yup.string()
        .required("Required"),
    physicalAddrZip: Yup.string()
        .required("Required")
        .test('Digits Only', 'The field should have digits only', digitsOnly)
        .test('Zip Code Format', 'Zip Code Format is not correct', zipRegExp)
        .min(5, 'Min Field Length: 5 digits')
        .max(10, 'Max Field Length: 10 digits'),
    mailingAddrLine1: Yup.string()
        .max(50, "Max Field Length: 50"),
    mailingAddrLine2: Yup.string().optional()
        .max(50, "Max Field Length: 50"),
    mailingAddrCity: Yup.string()
        .max(50, "Max Field Length: 50"),
    mailingAddrstateCD: Yup.string(),
    mailingAddrZip: Yup.string()
        .test('Digits Only', 'The field should have digits only', digitsOnly)
        .test('Zip Code Format', 'Zip Code Format is not correct', zipRegExp)
        .min(5, 'Min Field Length: 5 digits')
        .max(10, 'Max Field Length: 10 digits'),
    pocName: Yup.string()
        .required("Required")
        .max(50, "Max Field Length: 50"),
    pocTitle: Yup.string()
        .required("Required")
        .max(50, "Max Field Length: 50"),
    pocPhone: Yup.string()
        .required("Required")
        .matches(phoneRegExp, 'Contact Number is not valid'),
    pocEmail: Yup.string().email('Email is not valid')
        .required("Required")
        .max(50, "Max Field Length: 50"),
    comment: Yup.string()
        .optional()
        .max(4000, "Max Field Length: 4000")
});

// Custom validation function for conditional mailing address requirements
export const validateTesterForm = async (values: Tester): Promise<{ [key: string]: string }> => {
    const errors: { [key: string]: string } = {};

    const mailingAddressFieldsFilled =
        (values.mailingAddrLine1 && values.mailingAddrLine1.length > 0) ||
        (values.mailingAddrLine2 && values.mailingAddrLine2.length > 0) ||
        (values.mailingAddrCity && values.mailingAddrCity.length > 0) ||
        (values.mailingAddrstateCD && values.mailingAddrstateCD.length > 0) ||
        (values.mailingAddrZip && values.mailingAddrZip.length > 0);

    // If any mailing field is filled, all required fields must be filled
    if (mailingAddressFieldsFilled) {
        if (!values.mailingAddrLine1 || values.mailingAddrLine1.length === 0) {
            errors.mailingAddrLine1 = 'Required';
        }
        if (!values.mailingAddrCity || values.mailingAddrCity.length === 0) {
            errors.mailingAddrCity = 'Required';
        }
        if (!values.mailingAddrstateCD || values.mailingAddrstateCD.length === 0) {
            errors.mailingAddrstateCD = 'Required';
        }
        if (!values.mailingAddrZip || values.mailingAddrZip.length === 0) {
            errors.mailingAddrZip = 'Required';
        }
    }

    return errors;
};

export interface TesterVehicle {
    createdBy: string;
    createdDt: Date;
    incidentsCount: number;
    isActive: string;
    licensePlateNumber: string;
    otherEngineType: string;
    testerId: number;
    uniqueNumber: string;
    updatedBy: string;
    updatedDt: Date;
    vehicleDocuments: Document[];
    vehicleEngineType: string;
    vehicleId: number;
    vehicleMake: string;
    vehicleModel: string;
    vehicleRegistrationStateCd: string;
    vehicleTypeId: string;
    vin: string;
    year: number;

    documentId: number;
    documentGroupId: number;
    VehicleDocs: VehicleDocumentsViewModel;

    DocumentsUploaded: string[];
}

export const TesterVehicleValidationSchema = Yup.object().shape({
    vehicleTypeId: Yup.string()
        .required("Required"),
    vehicleEngineType: Yup.string()
        .required("Required"),
    otherEngineType: Yup.string().nullable().when('vehicleEngineType', (vehicleEngineType, schema) => {
        if (vehicleEngineType === 'Other') {
            return schema.required("Required");
        }
        return schema.optional();
    }),
    year: Yup.string()
        .required("Required")
        .test('Digits Only', 'The field should have digits only', digitsOnly),
    vehicleMake: Yup.string()
        .required("Required"),
    vehicleModel: Yup.string()
        .required("Required"),
    licensePlateNumber: Yup.string().when('vehicleTypeId', (vehicleTypeId, schema) => {
        if (vehicleTypeId === '1') {
            return schema.required("Required").max(50, "Max Field Length: 50");
        }
        return schema.optional();
    }),
    vin: Yup.string().when('vehicleTypeId', (vehicleTypeId, schema) => {
        if (vehicleTypeId === '1') {
            return schema.required("Required").max(17, "Max Field Length: 17");
        }
        return schema.optional();
    }),
    uniqueNumber: Yup.string().when('vehicleTypeId', (vehicleTypeId, schema) => {
        if (vehicleTypeId === '2') {
            return schema.required("Required").max(17, "Max Field Length: 17");
        }
        return schema.optional();
    }),
    vehicleRegistrationStateCd: Yup.string()
        .required("Required")
});

export interface VehicleDocumentsViewModel {
    vehicleId?: number;
    RegistrationDocument?: Document;
    VehicleDocumentGroup?: DocumentGroup;
}

export interface Document {
    createdBy?: string;
    createdDt?: Date;
    documentGroupId?: number;
    documentLocation?: string;
    documentTitle?: string;
    documentTypeId?: string;
    documentGroupDetails?: DocumentGroupDetails;
    updatedBy?: string;
    updatedDt?: Date;
    documentId?: number | null;
    vehicleId?: number | null;
    testerId?: number;
    isActive?: string;
    File?: File;
    document?: Document;
}

export interface DocumentGroup {
    IsNewGroup?: boolean;
    testerId: number;
    documentGroupId: number;
    documentGroupName: string;
    documents: Document[];
}

export interface DocumentGroupDetails {
    documentGroupName: string;
    documents: string[];
}

export interface VehicleIncidentsViewModel {
    vehicleId: number;
    incidentId: number;
    latitude: string;
    longitude: string;
    statusCode: string;
    incidentDate: Date;
    comment: string;
    updatedBy: string;
}

export interface SubmitMobileVehicleIncidentsViewModel {
    vehicleId: number;
    incidentId: number;
    latitude: string;
    longitude: string;
    statusCode: string;
    incidentDate: Date;
    comment: string;
    updatedBy: string;
}

export interface LookUp {
    id: number;
    key: string,
    value: ValueLookUp[];
}

export interface ValueLookUp {
    code: string;
    description: string;
    isActive: string;
}

export interface StateLookUp {
    StateCode: string;
    StateName: string;
    isActive: string;
    code: string;
    description: string;
}

export interface VehicleTypeLookUp {
    VehicleTypeCode: string;
    VehicleTypeName: string;
}

export interface DocumentTypeLookUp {
    DocumentTypeCode: string;
    DocumentTypeName: string;
}